import type { CaseSummary } from 'api/brokerCases';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from 'components/common';
import { EditIcon } from 'components/common/Icons';
import { useIsInternalUser } from 'hooks';
import { SortDirectionEnum } from 'legacy/shared-types';
import { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { btlApplicationFormApi } from 'api/btlApplicationForm';
import { getPageURLWithSection, PageURL } from 'router/pageURL';
import { allCapsToTitleCase, prettifyEnum } from 'common/helpers';
import { BtlApplicationFormPageEnum } from 'pages/BtlApplicationFormPage/types';
import { FormattedTableCell } from '../ApplicationsTable/FormattedTableCell';
import { BtlCaseSearchColumnKey, BtlCaseSearchRow, getBtlCaseSearchColumns } from './btlCasesSearchTable.config';

interface BtlCasesSearchTableProps {
  cases: CaseSummary[] | undefined;
  isLoading: boolean;
}

export const BtlCasesSearchTable: FC<BtlCasesSearchTableProps> = ({ cases, isLoading }) => {
  const isInternalUser = useIsInternalUser();
  const columns = getBtlCaseSearchColumns(isInternalUser);
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.DESC);
  const [orderBy, setOrderBy] = useState<BtlCaseSearchColumnKey>('createdAt');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const mapped: BtlCaseSearchRow[] =
      cases?.map(caseSummary => ({
        id: caseSummary.id,
        referenceNumber: caseSummary.referenceNumber,
        owner: caseSummary.owner,
        borrowerType: caseSummary.borrowerType ? allCapsToTitleCase(caseSummary.borrowerType) : undefined,
        status: prettifyEnum(caseSummary.status),
        purpose: allCapsToTitleCase(caseSummary.purpose),
        firmName: caseSummary.firmName,
        applicantNames: caseSummary.applicantNames,
        financingAmount: caseSummary.financingAmount,
        isTopSlicing: caseSummary.isTopSlicing,
        productCategory: caseSummary.productCategory ? allCapsToTitleCase(caseSummary.productCategory) : undefined,
        channel: caseSummary.channel ? allCapsToTitleCase(caseSummary.channel) : undefined,
        submittedAt: caseSummary.submittedAt,
        createdAt: caseSummary.createdAt,
        updatedAt: caseSummary.updatedAt,
      })) ?? [];

    return [...mapped].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null && bValue == null) {
        return 0;
      }
      if (aValue == null) {
        return order === SortDirectionEnum.ASC ? -1 : 1;
      }
      if (bValue == null) {
        return order === SortDirectionEnum.ASC ? 1 : -1;
      }
      if (aValue < bValue) {
        return order === SortDirectionEnum.ASC ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === SortDirectionEnum.ASC ? 1 : -1;
      }
      return 0;
    });
  }, [cases, order, orderBy]);

  function sortHandler(column: BtlCaseSearchColumnKey) {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === column;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(column);
  }

  async function applicationDetailsPreloadHandler(applicationId: string) {
    await queryClient.prefetchQuery(['btl-application-in-progress', applicationId], () =>
      btlApplicationFormApi.getApplicationInProgress(applicationId),
    );

    const editBtlApplicationUrl = getPageURLWithSection(
      PageURL.BTL_APPLICATION_EDIT,
      { id: applicationId },
      { section: BtlApplicationFormPageEnum.CASE_SUMMARY },
    );
    navigate(editBtlApplicationUrl);
  }

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table aria-label={'btl cases search table'}>
          <TableHead>
            <TableRow>
              <TableCell />
              {columns.map(columnName => (
                <TableCell key={columnName}>
                  <TableSortLabel
                    data-value={columnName}
                    active={orderBy === columnName}
                    direction={order}
                    onClick={() => sortHandler(columnName)}
                  >
                    <FormattedMessage id={`application.${columnName}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody isLoading={isLoading} columnsNumber={columns.length}>
            {rows.length ? (
              rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell />
                  {columns.map(columnName => (
                    <TableCell key={columnName}>
                      <FormattedTableCell objectKey={columnName} value={row[columnName]} />
                    </TableCell>
                  ))}
                  <TableCell>
                    <button
                      className={'cursor-pointer border-0 bg-transparent'}
                      onClick={() => applicationDetailsPreloadHandler(row.id)}
                    >
                      <EditIcon htmlColor={'grey'} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={columns.length}>
                  <FormattedMessage id={'common.itemsNotFound'} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
